/**
 * Lifecycle statuses.
 *
 * Plan D5: lifecycle statuses are `CHECK` constraints in PostgreSQL, not rows in
 * `reference_values`, because application code branches on them. This file is the
 * TypeScript half of that contract and MUST stay in step with the constraints in
 * `database/migrations/`. Changing one without the other is the exact
 * frontend/backend drift the shared package exists to prevent (Plan D8).
 *
 * Each status set is declared once as a `const` object and derives:
 *   - a union type      (`VolunteerStatus`)
 *   - a runtime array   (`VOLUNTEER_STATUSES`, for validators and dropdowns)
 */
// ---------------------------------------------------------------------------
// Volunteer — migration 003, volunteers_status_check
// ---------------------------------------------------------------------------
export const VOLUNTEER_STATUS = {
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
    PENDING: 'PENDING',
    REJECTED: 'REJECTED',
};
export const VOLUNTEER_STATUSES = Object.values(VOLUNTEER_STATUS);
/** How a volunteer record came into existence — migration 003, volunteers_source_check. */
export const VOLUNTEER_SOURCE = {
    PUBLIC_REGISTRATION: 'PUBLIC_REGISTRATION',
    STAFF_ENTRY: 'STAFF_ENTRY',
    MIGRATION: 'MIGRATION',
};
export const VOLUNTEER_SOURCES = Object.values(VOLUNTEER_SOURCE);
/**
 * What kind of volunteer this record is — migration 015, `volunteers_category_check`.
 *
 * A CHECK constraint rather than a `reference_values` pick-list, by Plan D5's
 * test: code branches on this value, so it must not be editable in NocoDB.
 *
 * Distinct from `VOLUNTEER_SOURCE`, which says how the record got here. A
 * corporate record is `STAFF_ENTRY` by definition — but so are most individuals,
 * which is why one field cannot carry both facts.
 *
 * `CORPORATE` means *the company itself is the volunteer*: one record per
 * company, reused across engagements, with the headcount on each attendance row
 * (Plan D15). A named employee of that company is `INDIVIDUAL` and carries the
 * same `organization_id`, so both roll up to one corporate total.
 */
export const VOLUNTEER_CATEGORY = {
    INDIVIDUAL: 'INDIVIDUAL',
    CORPORATE: 'CORPORATE',
    /** Schools, community groups, and whatever else turns up that is not a company. */
    OTHER: 'OTHER',
};
export const VOLUNTEER_CATEGORIES = Object.values(VOLUNTEER_CATEGORY);
/**
 * The category a registrant of this type becomes.
 *
 * The two enums are not the same set, deliberately: `VOLUNTEER_TYPE` is the
 * answer a visitor picks about themselves, `VOLUNTEER_CATEGORY` is the
 * structural kind of the master record. But they were also not connected at
 * all, and the consequence was that somebody registering through their
 * employer became an INDIVIDUAL volunteer with a company attached — a record
 * that contradicted itself.
 *
 * STUDENT maps to INDIVIDUAL rather than OTHER. A student volunteers as one
 * person; where they study is recorded in `institution`, and OTHER is for a
 * record that stands for a GROUP — a school sending a class, not a person who
 * attends one. `isGroupCategory` below is the rule that makes that matter:
 * a group record asks for a headcount on every attendance row.
 */
export function categoryForVolunteerType(type) {
    return type === VOLUNTEER_TYPE.CORPORATE
        ? VOLUNTEER_CATEGORY.CORPORATE
        : VOLUNTEER_CATEGORY.INDIVIDUAL;
}
/** Whether a record of this category stands for more than one person. */
export function isGroupCategory(category) {
    return category !== VOLUNTEER_CATEGORY.INDIVIDUAL;
}
/**
 * How a person says they are volunteering, on the public form — migration 023,
 * `registration_requests_volunteer_type_check`.
 *
 * Deliberately NOT the same set as `VOLUNTEER_CATEGORY`. This is the answer a
 * visitor picks about themselves; that is the structural kind of the master
 * record. Every public registrant is one person, so the `Volunteer` they become
 * is always `INDIVIDUAL` — `CORPORATE` here means *an employee volunteering
 * through their company*, and its only lasting effect is the `organization_id`
 * it carries onto that volunteer (the "named employee" case in Plan D15).
 * `STUDENT` records the affiliation on the submission and needs no column of its
 * own on the volunteer.
 */
export const VOLUNTEER_TYPE = {
    INDIVIDUAL: 'INDIVIDUAL',
    CORPORATE: 'CORPORATE',
    STUDENT: 'STUDENT',
};
export const VOLUNTEER_TYPES = Object.values(VOLUNTEER_TYPE);
/**
 * How often a volunteer wants to help (registration spec) — a CHECK the way
 * `status` is, not a `reference_values` pick-list. `OTHER` is the open answer
 * (13 Sep 2026): the form reveals a free-text box and stores what they typed in
 * `volunteering_frequency_other`, the same shape as occupation's OTHER.
 */
export const VOLUNTEERING_FREQUENCY = {
    WEEKLY: 'WEEKLY',
    MONTHLY: 'MONTHLY',
    QUARTERLY: 'QUARTERLY',
    OCCASIONALLY: 'OCCASIONALLY',
    OTHER: 'OTHER',
};
export const VOLUNTEERING_FREQUENCIES = Object.values(VOLUNTEERING_FREQUENCY);
/**
 * Which age band a volunteer falls in (registration spec, 13 Sep 2026) — it
 * replaces date of birth on the public form. A fixed set, so a CHECK. Bands are
 * non-overlapping so every age lands in exactly one.
 */
export const AGE_GROUP = {
    AGE_18_24: 'AGE_18_24',
    AGE_25_34: 'AGE_25_34',
    AGE_35_44: 'AGE_35_44',
    AGE_45_54: 'AGE_45_54',
    AGE_55_PLUS: 'AGE_55_PLUS',
};
export const AGE_GROUPS = Object.values(AGE_GROUP);
/** The label each age band wears on screen. */
export const AGE_GROUP_LABELS = {
    AGE_18_24: '18–24',
    AGE_25_34: '25–34',
    AGE_35_44: '35–44',
    AGE_45_54: '45–54',
    AGE_55_PLUS: '55+',
};
/** The label for an age-group code, or '' for none. Unknown codes pass through. */
export function ageGroupLabel(code) {
    if (code === null || code === undefined || code === '')
        return '';
    return AGE_GROUP_LABELS[code] ?? code;
}
/**
 * What a volunteer does for a living (registration spec, 12 Sep 2026).
 *
 * A fixed set, so a CHECK constraint the way `status` and `volunteering_frequency`
 * are — NOT a `reference_values` pick-list. Two things put it on this side of the
 * D5 line. It is Parinaam's list to settle, not staff's to grow in NocoDB. And
 * the public form BRANCHES on `OTHER` to reveal a "please specify" box — the very
 * thing D5 keeps out of reference categories, whose whole point is that no code
 * depends on which value it holds. `institution` (migration 035) went the other
 * way for the opposite reasons: an open-ended list of colleges, and no code that
 * cares which one.
 */
/**
 * The `INSTITUTION` reference code that ends the student's picker.
 *
 * A code rather than a row this time, because institutions are
 * `reference_values` and that list already carried "Other — not listed" — it
 * just had nowhere to put the college behind it. The detail now travels in
 * `institution_other` (migration 045), the same shape as occupation's.
 */
export const INSTITUTION_OTHER = 'OTHER';
/**
 * The "Other — not listed" partner, seeded by migration 045.
 *
 * Corporate volunteers had no escape hatch at all: the employer list is
 * staff-maintained on purpose — a free-text employer is how one company ends up
 * recorded three ways — and the cost was a visitor who IS corporate, for whom
 * the field is mandatory, having nowhere to go. Parinaam chose the messier data
 * over the dead end (13 Oct 2026).
 *
 * It is a real `organizations` row rather than a sentinel string because
 * `volunteers_group_has_organization` requires a corporate volunteer to have an
 * `organization_id`, and that check is worth keeping. What they typed goes in
 * `organization_other`, and staff repoint them at the real partner afterwards.
 *
 * Matched by ID, never by name. A rename in NocoDB would otherwise stop the
 * form asking for the detail, and nothing would look broken.
 */
export const OTHER_ORGANIZATION_ID = '0e000000-0000-4000-8000-00000000000f';
export const OCCUPATION = {
    SALARIED: 'SALARIED',
    RETIRED: 'RETIRED',
    HOMEMAKER: 'HOMEMAKER',
    BUSINESS_OWNER: 'BUSINESS_OWNER',
    FREELANCER: 'FREELANCER',
    /** The escape hatch — the form asks for the detail in `occupation_other`. */
    OTHER: 'OTHER',
};
export const OCCUPATIONS = Object.values(OCCUPATION);
/**
 * The label each code wears on every screen. Parinaam's exact wording, kept here
 * as the single source rather than retyped per form — and `humaniseCode` would
 * flatten "Business owner / entrepreneur" to "Business owner".
 */
export const OCCUPATION_LABELS = {
    SALARIED: 'Salaried professional / working professional',
    RETIRED: 'Retired',
    HOMEMAKER: 'Homemaker',
    BUSINESS_OWNER: 'Business owner / entrepreneur',
    FREELANCER: 'Freelancer / consultant',
    OTHER: 'Other',
};
/**
 * The label for an occupation code, or '' for none. An unrecognised code — one a
 * screen is holding from before a code was retired — is handed back as-is rather
 * than dropped, the same forgiveness `humaniseCode` shows. Note this does NOT
 * resolve OTHER to its specify text: that detail lives in `occupation_other`, and
 * only the caller holding the whole record can substitute it.
 */
export function occupationLabel(code) {
    if (code === null || code === undefined || code === '')
        return '';
    return OCCUPATION_LABELS[code] ?? code;
}
/**
 * Who has to sign a session off (migration 038, `sessions_responsibility_check`).
 * A fixed set, so a CHECK the way statuses are — not a `reference_values` list.
 *   PARINAAM       the Parinaam team alone
 *   VOLUNTEER      the volunteer alone (a corporate partner/host is a volunteer)
 *   COLLABORATIVE  both, and both must confirm
 *
 * Arrived as `PHASE_RESPONSIBILITY` on the branch that added session authoring,
 * against a model where a phase held this. Phases are gone and the column is
 * `sessions.responsibility`, so it is named for the thing that carries it. The
 * three values never changed.
 */
export const SESSION_RESPONSIBILITY = {
    PARINAAM: 'PARINAAM',
    VOLUNTEER: 'VOLUNTEER',
    COLLABORATIVE: 'COLLABORATIVE',
};
export const SESSION_RESPONSIBILITIES = Object.values(SESSION_RESPONSIBILITY);
/** The label each responsibility wears on screen. */
export const SESSION_RESPONSIBILITY_LABELS = {
    PARINAAM: 'Parinaam team',
    VOLUNTEER: 'The volunteer',
    COLLABORATIVE: 'Both (collaborative)',
};
// ---------------------------------------------------------------------------
// Registration request — migration 004, registration_requests_status_check
// ---------------------------------------------------------------------------
export const REGISTRATION_STATUS = {
    SUBMITTED: 'SUBMITTED',
    UNDER_REVIEW: 'UNDER_REVIEW',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
};
export const REGISTRATION_STATUSES = Object.values(REGISTRATION_STATUS);
// ---------------------------------------------------------------------------
// Program — migration 005, programs_status_check
// ---------------------------------------------------------------------------
export const PROGRAM_STATUS = {
    DRAFT: 'DRAFT',
    ACTIVE: 'ACTIVE',
    COMPLETED: 'COMPLETED',
    INACTIVE: 'INACTIVE',
    /**
     * Deleted, and final.
     *
     * A soft delete rather than a `DELETE`: the program's activities carry
     * attendance, and attendance is the record of hours people actually gave.
     * Removing the row would take that with it, or be refused by the foreign keys
     * halfway through — so the row stays and stops counting.
     *
     * Nothing leaves this state. It is absent from every other status's transition
     * list, so `PATCH /programs/:id` can neither reach it nor escape it; the only
     * way in is `DELETE /programs/:id`, and there is no way back out at all.
     */
    DELETED: 'DELETED',
};
export const PROGRAM_STATUSES = Object.values(PROGRAM_STATUS);
/**
 * The statuses a form or a request body may *set*.
 *
 * `DELETED` is reachable only through `DELETE /programs/:id`, which takes a
 * reason and writes an audit row. Leaving it in the list a create body validates
 * against would let `POST /programs {"status":"DELETED"}` walk straight past
 * that — and a status dropdown offering "Deleted" alongside "Active" invites
 * exactly the unconsidered click the delete panel exists to prevent.
 *
 * The full `PROGRAM_STATUSES` is still what a *filter* should use: a deleted
 * program is findable, it just cannot be created or edited into being.
 */
export const PROGRAM_STATUSES_SETTABLE = PROGRAM_STATUSES.filter((status) => status !== PROGRAM_STATUS.DELETED);
// ---------------------------------------------------------------------------
// Activity — migration 006, activities_status_check
// ---------------------------------------------------------------------------
export const ACTIVITY_STATUS = {
    DRAFT: 'DRAFT',
    SCHEDULED: 'SCHEDULED',
    COMPLETED: 'COMPLETED',
    CANCELLED: 'CANCELLED',
    /**
     * Deleted, and final — see `PROGRAM_STATUS.DELETED`.
     *
     * Distinct from `CANCELLED`, and the difference is what it says about the
     * hours. Cancelled means the activity did not happen but the record of it is
     * still part of the program's history, and reverses in one click. Deleted
     * means the activity should never have been there: its attendance stops being
     * counted anywhere, and it cannot be brought back.
     */
    DELETED: 'DELETED',
};
export const ACTIVITY_STATUSES = Object.values(ACTIVITY_STATUS);
/** Settable by a form or a body. See `PROGRAM_STATUSES_SETTABLE`. */
export const ACTIVITY_STATUSES_SETTABLE = ACTIVITY_STATUSES.filter((status) => status !== ACTIVITY_STATUS.DELETED);
// ---------------------------------------------------------------------------
// Assignment — migration 007, assignments_status_check
// ---------------------------------------------------------------------------
export const ASSIGNMENT_STATUS = {
    ASSIGNED: 'ASSIGNED',
    CANCELLED: 'CANCELLED',
    COMPLETED: 'COMPLETED',
};
export const ASSIGNMENT_STATUSES = Object.values(ASSIGNMENT_STATUS);
// ---------------------------------------------------------------------------
// Attendance — migration 008, attendance_status_check
// ---------------------------------------------------------------------------
/**
 * Plan D2: three states only. `DRAFT`/`SUBMITTED`/`CORRECTED` from Section 10.18
 * are rejected — a correction is an audit *event*, not a status, otherwise
 * "corrected and approved" cannot be represented.
 */
export const ATTENDANCE_STATUS = {
    PENDING: 'PENDING',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
};
export const ATTENDANCE_STATUSES = Object.values(ATTENDANCE_STATUS);
/**
 * The only legal attendance transitions (Section 8.31, Plan D2).
 *
 * Enforced by the service layer, not by the database: PostgreSQL can constrain the
 * *set* of values but not the moves between them. Re-approving an approved record
 * and `REJECTED -> APPROVED` are both refused.
 */
export const ATTENDANCE_STATUS_TRANSITIONS = {
    PENDING: [ATTENDANCE_STATUS.APPROVED, ATTENDANCE_STATUS.REJECTED],
    APPROVED: [],
    REJECTED: [],
};
export function canTransitionAttendance(from, to) {
    return ATTENDANCE_STATUS_TRANSITIONS[from].includes(to);
}
// ---------------------------------------------------------------------------
// The rest of the lifecycle matrix (Section 8.43, M4-B)
// ---------------------------------------------------------------------------
/**
 * Every lifecycle, written down the way attendance already was.
 *
 * Until M4-B only attendance had its moves declared as data. The others were
 * whatever the service layer happened to check, which meant nobody could tell an
 * intentionally permissive lifecycle from one where the guard was simply never
 * written. `ACTIVE -> PENDING` on a volunteer was allowed — not by decision, but
 * because nothing looked.
 *
 * Two rules shape every map below.
 *
 * **No self-transitions.** No map lists its own state. On the `PATCH` paths this
 * is belt-and-braces — `changedFields` drops a value equal to the stored one
 * before the guard ever sees it, so re-saving a form has always been a no-op.
 * It bites on the endpoints that name a transition outright: approving an
 * already-approved attendance row, cancelling a cancelled assignment.
 *
 * **No dead ends without a reason.** D16 was the lesson: three rules that were
 * each right alone combined to make a volunteer's real hours permanently
 * unrecordable. A state is terminal here only where reversing it would
 * contradict data that already exists, never merely because the move looks odd.
 * A mistaken rejection is recoverable; a rejected *attendance* row is not, and
 * does not need to be, because a fresh record can be taken instead.
 */
/**
 * Volunteer lifecycle.
 *
 * `PENDING` is the state a public submission lands in (Plan D11) and staff decide
 * it. `INACTIVE` is retirement, and it is reversible in both directions because
 * volunteers come back — the `volunteer.activated` event carries `previous_status`
 * precisely so n8n can greet a returner differently from a newcomer.
 *
 * Two moves are refused deliberately **for the named staff operations**:
 *
 *   - **`ACTIVE`/`INACTIVE` back to `PENDING`.** An approval cannot be un-made
 *     into "undecided". The submission that produced it is preserved either way.
 *   - **`ACTIVE`/`INACTIVE` to `REJECTED`.** Rejection means *never accepted*;
 *     parting company with someone who was accepted is `INACTIVE`. Keeping the
 *     two apart is what lets "how many people did we turn away" stay answerable.
 *
 * `REJECTED -> ACTIVE` is allowed, and always has been: staff reject from a
 * queue, quickly, on thin information, and `POST /volunteers/:id/activate`
 * carries the training gate that move needs.
 *
 * ---------------------------------------------------------------------------
 * What this map does NOT govern (Parinaam, 22 Sep 2026)
 * ---------------------------------------------------------------------------
 * An admin may set a volunteer to **any** status, from any status, through
 * `POST /volunteers/:id/status`. That operation does not consult this map and
 * is not an omission — Parinaam asked for the status to be theirs to correct
 * without the system arguing, after a rejection made on thin information proved
 * close to impossible to take back.
 *
 * So read this map as **the moves ordinary staff may make through the named
 * operations**, not as the set of moves the database can hold. The distinction
 * matters when reading `canTransitionVolunteer`: a `false` from it means "no
 * named operation does this", never "this cannot happen".
 *
 * The registration behind a volunteer is settled by whichever route is taken.
 * It records what was decided at the time; the volunteer record carries what is
 * true now.
 */
export const VOLUNTEER_STATUS_TRANSITIONS = {
    PENDING: [VOLUNTEER_STATUS.ACTIVE, VOLUNTEER_STATUS.REJECTED],
    ACTIVE: [VOLUNTEER_STATUS.INACTIVE],
    INACTIVE: [VOLUNTEER_STATUS.ACTIVE],
    // `/activate` is the named operation that takes this route, and it carries the
    // training gate. An admin needing any other destination uses the override,
    // which does not consult this map at all.
    REJECTED: [VOLUNTEER_STATUS.ACTIVE],
};
/**
 * Every status an admin may set directly, through `POST /volunteers/:id/status`.
 *
 * The whole list, deliberately (Parinaam, 22 Sep 2026). This is the one
 * operation in the system that does not consult
 * `VOLUNTEER_STATUS_TRANSITIONS`: the request was a dropdown holding every
 * status, with the system not arguing about which one is reachable from where.
 *
 * It exists because the named operations are shaped around what is *usually*
 * right, and a correction is the case where that shape is the problem. A
 * rejection entered against the wrong person, a volunteer marked inactive by a
 * mis-click, a record imported in the wrong state — each is a typo in a column,
 * not a decision being re-made, and the training gate and the transition map
 * both read them as decisions.
 *
 * Two things it is NOT:
 *
 *   - **Not a replacement for the named operations.** `/activate`,
 *     `/deactivate` and `/reject` stay exactly as they were, keep their guards,
 *     and stay open to all staff. This is the admin's way round them, not
 *     everybody's way past them.
 *   - **Not silent.** It writes the same `STATUS_CHANGE` audit row, settles the
 *     registration behind the volunteer, and fires `volunteer.activated` on the
 *     way into `ACTIVE`, so an admin-set activation reaches n8n like any other.
 */
export const VOLUNTEER_ADMIN_STATUS_TARGETS = VOLUNTEER_STATUSES;
export function canTransitionVolunteer(from, to) {
    return VOLUNTEER_STATUS_TRANSITIONS[from].includes(to);
}
/**
 * Registration lifecycle.
 *
 * Terminal once decided, which is the one place a dead end is correct: the
 * submission is a historical record of what arrived and what was concluded, not a
 * live object. Reconsidering happens on the volunteer record, which is why
 * `REJECTED -> ACTIVE` exists there and has no counterpart here.
 *
 * `UNDER_REVIEW` is optional — staff may decide straight from `SUBMITTED`.
 */
export const REGISTRATION_STATUS_TRANSITIONS = {
    SUBMITTED: [
        REGISTRATION_STATUS.UNDER_REVIEW,
        REGISTRATION_STATUS.APPROVED,
        REGISTRATION_STATUS.REJECTED,
    ],
    UNDER_REVIEW: [REGISTRATION_STATUS.APPROVED, REGISTRATION_STATUS.REJECTED],
    APPROVED: [],
    REJECTED: [],
};
export function canTransitionRegistration(from, to) {
    return REGISTRATION_STATUS_TRANSITIONS[from].includes(to);
}
/**
 * Program lifecycle.
 *
 * Deliberately permissive apart from self-transitions, and that is the finding
 * rather than an omission: a program is a container with dates, and staff
 * reorganising their own planning is not a data-integrity question. `COMPLETED`
 * reopens because "we're running it again next term" is ordinary.
 */
export const PROGRAM_STATUS_TRANSITIONS = {
    DRAFT: [PROGRAM_STATUS.ACTIVE, PROGRAM_STATUS.INACTIVE, PROGRAM_STATUS.COMPLETED],
    ACTIVE: [PROGRAM_STATUS.COMPLETED, PROGRAM_STATUS.INACTIVE, PROGRAM_STATUS.DRAFT],
    COMPLETED: [PROGRAM_STATUS.ACTIVE, PROGRAM_STATUS.INACTIVE],
    INACTIVE: [PROGRAM_STATUS.ACTIVE, PROGRAM_STATUS.DRAFT, PROGRAM_STATUS.COMPLETED],
    /**
     * Empty, and not an oversight: deletion is final.
     *
     * `DELETED` is also absent from all four lists above, which is the other
     * half of the same rule — `PATCH` cannot put a program here, so the
     * deliberate `DELETE` endpoint is the only door, and this empty list means
     * there is no door out. Any future status added above must not list it.
     */
    DELETED: [],
};
export function canTransitionProgram(from, to) {
    return PROGRAM_STATUS_TRANSITIONS[from].includes(to);
}
/**
 * Activity lifecycle.
 *
 * `DRAFT -> COMPLETED` is allowed on purpose: an activity that already happened
 * is often entered afterwards, and forcing staff to schedule a past event before
 * marking it done would be a step that exists only to satisfy the diagram.
 *
 * `CANCELLED` reverses, because cancelling is one click and assignments are not
 * destroyed by it — only new ones are refused while it stands. `COMPLETED`
 * reverses too, since hours turn up late and reopening beats recording them
 * against the wrong activity.
 *
 * `COMPLETED -> CANCELLED` is allowed for the same reason, and it was the map
 * getting corrected by a test rather than the other way round: "we marked it
 * done and it never actually ran" is one intention, and routing it through
 * `SCHEDULED` would be a detour invented by the diagram.
 *
 * What this map does *not* police is whether attendance already exists. Whether
 * an activity with approved hours may be reopened or cancelled is a question
 * about data, not about which states adjoin, and it belongs where the data is.
 */
export const ACTIVITY_STATUS_TRANSITIONS = {
    DRAFT: [ACTIVITY_STATUS.SCHEDULED, ACTIVITY_STATUS.COMPLETED, ACTIVITY_STATUS.CANCELLED],
    SCHEDULED: [ACTIVITY_STATUS.COMPLETED, ACTIVITY_STATUS.CANCELLED, ACTIVITY_STATUS.DRAFT],
    COMPLETED: [ACTIVITY_STATUS.SCHEDULED, ACTIVITY_STATUS.CANCELLED],
    CANCELLED: [ACTIVITY_STATUS.DRAFT, ACTIVITY_STATUS.SCHEDULED],
    /** Final. See `PROGRAM_STATUS_TRANSITIONS.DELETED` — same rule, same reason. */
    DELETED: [],
};
export function canTransitionActivity(from, to) {
    return ACTIVITY_STATUS_TRANSITIONS[from].includes(to);
}
/**
 * Assignment lifecycle.
 *
 * `CANCELLED` is terminal, and here the dead end is the right answer rather than
 * an oversight: re-assigning somebody is a new assignment, and the create path
 * already treats a cancelled row as absent so it succeeds. Nothing is lost and
 * the roster keeps both facts — that they were taken off, and that they were put
 * back — instead of one row that quietly flips.
 */
export const ASSIGNMENT_STATUS_TRANSITIONS = {
    ASSIGNED: [ASSIGNMENT_STATUS.CANCELLED, ASSIGNMENT_STATUS.COMPLETED],
    COMPLETED: [ASSIGNMENT_STATUS.CANCELLED],
    CANCELLED: [],
};
export function canTransitionAssignment(from, to) {
    return ASSIGNMENT_STATUS_TRANSITIONS[from].includes(to);
}
// ---------------------------------------------------------------------------
// Audit — migration 009
// ---------------------------------------------------------------------------
export const AUDIT_ENTITY_TYPE = {
    VOLUNTEER: 'VOLUNTEER',
    REGISTRATION_REQUEST: 'REGISTRATION_REQUEST',
    PROGRAM: 'PROGRAM',
    ACTIVITY: 'ACTIVITY',
    ASSIGNMENT: 'ASSIGNMENT',
    ATTENDANCE: 'ATTENDANCE',
    /** M3, Plan D15 — corporate partners are edited by staff like anything else. */
    ORGANIZATION: 'ORGANIZATION',
    /**
     * A dated unit of work inside an activity, confirmed by the party
     * responsible. Rows written against PHASE before 13 Sep 2026 still exist in
     * `audit_log` and are deliberately not rewritten — an audit trail that is
     * edited to match a later model is not an audit trail.
     */
    SESSION: 'SESSION',
    BENEFICIARY_COMMUNITY: 'BENEFICIARY_COMMUNITY',
    CERTIFICATE: 'CERTIFICATE',
};
export const AUDIT_ENTITY_TYPES = Object.values(AUDIT_ENTITY_TYPE);
export const AUDIT_ACTION = {
    CREATE: 'CREATE',
    UPDATE: 'UPDATE',
    APPROVE: 'APPROVE',
    REJECT: 'REJECT',
    STATUS_CHANGE: 'STATUS_CHANGE',
    CORRECTION: 'CORRECTION',
    OVERRIDE: 'OVERRIDE',
    CANCEL: 'CANCEL',
    /**
     * D21 — one party of a session saying their side is done. Distinct from
     * STATUS_CHANGE: a collaborative session collects two of these before its
     * status moves at all, and losing the first would lose who spoke first.
     *
     * The value is still the string 'PHASE_CONFIRMED'. Renaming it would orphan
     * every audit row written before 13 Sep 2026 — the history would read as a
     * gap rather than as the same act under an older name.
     */
    SESSION_CONFIRMED: 'PHASE_CONFIRMED',
    /**
     * Plan D12: a volunteer record destroyed. Only ever a submission that was
     * never activated and has no assignments and no attendance — §5.8 protects
     * everyone else. The audit row outlives the record it describes.
     */
    DELETE: 'DELETE',
    /** Plan D3: attendance recorded for a volunteer with no assignment. */
    ATTENDANCE_WITHOUT_ASSIGNMENT: 'ATTENDANCE_WITHOUT_ASSIGNMENT',
    /** Plan D4: staff confirmed an overlapping activity. */
    OVERLAP_ACCEPTED: 'OVERLAP_ACCEPTED',
    /**
     * A recognition certificate issued (migration 034). Deliberately not `CREATE`:
     * the row being written is incidental, and what the trail needs to show is
     * that a document went out claiming a figure — so the audit entry carries the
     * minutes and the activity count it asserted.
     */
    ISSUE: 'ISSUE',
};
export const AUDIT_ACTIONS = Object.values(AUDIT_ACTION);
// ---------------------------------------------------------------------------
// Reference data categories — Plan D5, seeded by database/seeds/reference_data.sql
// ---------------------------------------------------------------------------
/**
 * Categories in `reference_values`. Staff maintain the *values* in NocoDB; the
 * category names are structural, so they live here. No code branches on the codes
 * inside a category — that is the whole point of the D5 split.
 */
/**
 * Who an account is.
 *
 * Defined here rather than in the API and the portal separately, which is how
 * it was until the password policy needed the same list on both sides. Two
 * hand-maintained copies of a role list drift silently: the compiler is happy
 * with each of them.
 *
 * `TEAM` is what the database calls a field coordinator. The portal says
 * "Parinaam team"; the column has said TEAM since migration 028 and renaming
 * it would be a migration for a word.
 */
export const USER_ROLE = {
    ADMIN: 'ADMIN',
    TEAM: 'TEAM',
    VOLUNTEER: 'VOLUNTEER',
};
export const USER_ROLES = Object.values(USER_ROLE);
export const REFERENCE_CATEGORY = {
    GENDER: 'GENDER',
    AREA_OF_INTEREST: 'AREA_OF_INTEREST',
    /*
     * AVAILABILITY was here until 13 Sep 2026. Its rows are still seeded and its
     * columns still hold data, but no field collects it and no screen shows it —
     * see the note on `Volunteer` in entities.ts, and migration 038.
     */
    LANGUAGE: 'LANGUAGE',
    /** How a volunteer prefers to be contacted (M3, D15). Multi-select. */
    CONTACT_METHOD: 'CONTACT_METHOD',
    /** Skills a volunteer can offer (registration spec). Staff-maintained, multi-select. */
    SKILL: 'SKILL',
    /**
     * Where a student studies (8 Sep 2026). Single-select, shown on the public
     * form only for `VOLUNTEER_TYPE.STUDENT`.
     *
     * A pick-list rather than free text for the reason migration 015 gave about
     * corporate partners: three spellings of one college cannot be counted.
     */
    INSTITUTION: 'INSTITUTION',
    /**
     * How a volunteer heard about Parinaam (13 Sep 2026). Single-select, asked of
     * everybody on the public form and optional.
     *
     * Short and closed on purpose: these are channels Parinaam can act on, not a
     * description of how somebody came to hear something. The story belongs in
     * the notes field the form already has.
     */
    REFERRAL_SOURCE: 'REFERRAL_SOURCE',
};
export const REFERENCE_CATEGORIES = Object.values(REFERENCE_CATEGORY);
//# sourceMappingURL=status.js.map